import { MangoQuery, RxChangeEvent, RxCollection, RxDocument, RxJsonSchema } from 'rxdb'
import { LOGGER, combine, flatten } from '@cicero/data/utils'
import { Store } from '../services/Store'
import { ModelService } from '../services/ModelService'

export interface IModel<T> {
  CONFIG: IModelSchemaConfig
  LIFECYCLE: IModelLifecycle<T>
  NAME: string
}

export interface IModelLifecycle<T = any> {
  afterCreate?(store: Store, doc: RxDocument<T>): Promise<any> // Called outside of RxDB - has access to the store and doc to perform side effects
  afterSync?(store: Store, doc: T[], changes: T[]): Promise<T>
  afterSyncDeletion?(store: Store, doc: T): Promise<T>
  afterSyncError?(store: Store, doc: T, error: any): void
  cacheKey(schema: IModel<T>, params: IQuery<T>): string // Given a query, generate a key to cache this query
  cachePeriod(schema: IModel<T>, params: IQuery<T>): number // Given a query, how long should we cache it for
  fetch?(store: Store, params: IQuery<T>): Promise<any> // Load a single entity from the API
  fetchDispatch(store: Store, service: ModelService<any>, params: IQuery<T>): void
  fetchQuery?(store: Store, params: IQuery<T>): Promise<any[]> // Query multiple entities from the API
  fetchQueryDispatch(store: Store, service: ModelService<any>, params: IQuery<T>): void
  loadSyncQueue(coll: RxCollection<T>): Promise<T[]> // Load entities that have local changes to sync
  merge(current: T, changes: T): T // Given a current doc, merge in some changes, deciding how to handle conflicts
  onChange(store: Store, change: RxChangeEvent<T>): any
  preInit?(schema: IModel<T>): IModel<T>
  preChange(doc: T): any // Called both preSave and preInsert
  preInsert?(doc: T): any // Called before creating a new doc
  preSave?(doc: T): any // Called before saving an existing doc
  preSync(doc: T, schema: IModelSchemaConfig): T // Called before syncing a doc
  sanitize(schema: IModel<T>, params: any): T // Clean up and remove params that aren't allowed
  transformQuery(q: IQuery<T>): IQuery<T> // Transforms any query before it is used in a find/findOne operation
}

export interface IModelSchemaConfigParams {
  name: string
  name_plural?: string
  description: string
  associations?: () => IModelAssociationSchema[]
  indexes?: any[]
  lifecycle?: Partial<IModelLifecycle>
  local?: string[]
  required?: any[]
  sync?: boolean
  version: number
}

export interface IModelSchemaConfig extends IModelSchemaConfigParams {
  initialize(): void
  initialized?: boolean
  local: string[]
  name_plural: string
  properties?: { [key: string]: IModelAttrSchema }
  sync: boolean
}

export interface IModelSchema<T, M = any, CM = any> {
  name: string
  schema: RxJsonSchema<T>
  methods?: M
  statics?: CM
  options?: any
}

export interface IModelAttrSchema {
  type: string|string[]
  default?: any
  ref?: string
  primary?: boolean
  format?: string
  properties?: { [key: string]: IModelAttrSchema }
  items?: IModelAttrSchema
  index?: boolean
  maximum?: number
  minimum?: number
  required?: boolean
  local?: boolean
}

export type IAssociationType = 'hasOne'|'belongsTo'|'hasMany'|'belongsToMany'
export type Without<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>

export interface IModelAssociationSchema {
  type: IAssociationType
  model: IModel<any>
  key?: string
  foreignKey?: string
}

export interface IQuery<T = any> extends MangoQuery<T> {
  modifier?: { [key: string]: any } // Used to attach query params to a request
  with_deleted?: boolean // Unless passed as true, queries will ignore any locally deleted docs
}

const constructEmptySchema = (): IModelSchemaConfig => {
  return {
    indexes: [],
    required: [],
    local: [],
    properties: {},
  } as any
}

const schemas = new Map<any, Partial<IModelSchemaConfig>>()
// const associations = new Map<any, IModelAssociationSchema[]>()

export const PARSE_CONFIG_AS_SCHEMA = (config: IModelSchemaConfig): IModelSchema<any> => {
  const { name, name_plural, description, indexes, properties, required, version } = config
  return {
    name: name_plural || name,
    schema: {
      description,
      indexes,
      type: 'object',
      properties: { ...properties } as any,
      required,
      version,
    },
  }
}

/**
 * Turns the given class into a valid model schema that can be loaded by a store
 * @param config 
 */
export const ModelSchema = (config: IModelSchemaConfigParams) => {
  return <U extends IModel<any>>(constructor: U) => {
    const { name, name_plural, required, indexes, sync, local, associations, lifecycle } = config
    const schema = schemas.get(constructor) || constructEmptySchema()
    const baseSchema = schemas.get((constructor as any).__proto__) // TODO May need to do this recursively for multiple extends?

    constructor.NAME = name
    constructor.LIFECYCLE = combine(constructor.LIFECYCLE, lifecycle)
    constructor.CONFIG = {
      ...config,
      indexes: flatten(baseSchema?.indexes, schema.indexes, indexes),
      initialized: false,
      local: flatten(baseSchema?.local, schema.local, local),
      name_plural: name_plural || `${name}s`,
      properties: combine(baseSchema?.properties, schema.properties),
      required: flatten(baseSchema?.required, schema.required, required),
      sync: sync !== false,
      initialize() {
        if (constructor.CONFIG.initialized) {
          LOGGER.debug(`${name} is already initialized, cannot initialize twice.`)
          return
        }
        constructor.CONFIG.initialized = true
        constructor.CONFIG.properties = combine(
          constructor.CONFIG.properties,
          associations ? parseAssociationsAsProperties(associations()) : {}
        )
      },
    }

    LOGGER.debug(`[SCHEMA_${config.name}]`, constructor.CONFIG)
  }
}

/**
 * Makes a given schema key an attribute on the given model schema
 * @param config 
 */
export const Attr = (config: IModelAttrSchema) => {
  return function (target: any, key: string) {
    const schema = schemas.get(target.constructor) || constructEmptySchema()

    if (config.index && schema.indexes) {
      schema.indexes.push(key)
      delete config.index
    }

    if (config.required && schema.required) {
      schema.required.push(key)
      delete config.required
    }

    if (config.local && schema.local) {
      schema.local.push(key)
      delete config.local
    }

    schema.properties = { ...schema.properties, [key]: config }
    schemas.set(target.constructor, schema)
  }
}

export const parseAssociationsAsProperties = (assocs: IModelAssociationSchema[]): { [key: string]: IModelAttrSchema } => {
  return assocs.reduce((acc: any, assoc) => {
    const { key, type, model, foreignKey } = assoc
    if (!key) {
      LOGGER.warn('association has no key', assoc)
    } else {
      const schema: any = {}
      switch (type) {
        case 'belongsTo':
        case 'hasOne':
          schema.type = 'string'
          break
        case 'belongsToMany':
        case 'hasMany':
          schema.type = 'array'
          schema.items = { type: 'string' }
          break
      }

      switch (typeof model) {
        case 'string':
          schema.ref = model
          break
        default:
          const castModel: any = model
          const m: IModel<any> = castModel['NAME'] ? castModel : castModel()
          schema.ref = m.CONFIG.name_plural
      }

      acc[foreignKey || key] = schema
    }

    return acc
  }, {})
}
